import type { AccessibleRole } from "../core/accessibility.js";
import type { Container } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import { tabOrder } from "../core/focus.js";
import { isDesktopWindow, type DesktopWindow } from "../core/Desktop.js";

/**
 * What the navigation index lists, worked out without drawing anything.
 *
 * Named for what it holds rather than `navigationIndex.ts`, which is how the
 * pure halves beside `TextBox` and `FlipCard` are named - and which would
 * anyway be the same file as `NavigationIndex.ts` on Windows.
 */
export interface NavEntry {
  control: Control;
  /** A window, a landmark inside one, or somewhere focus can land. */
  kind: "window" | "landmark" | "stop";
  /** 0 for a window, 1 for what is inside it. */
  depth: number;
  name: string;
  role: AccessibleRole;
  active?: boolean;
  minimized?: boolean;
}

/** Roles worth listing on their own, above the individual tab stops. */
const LANDMARKS = new Set<AccessibleRole>([
  "menubar",
  "toolbar",
  "tablist",
  "status",
  "tree",
  "listbox",
  "grid",
  "region",
  "group",
]);

interface Minimizable {
  minimized?: unknown;
}

/**
 * An outline of what is on the desktop and where focus can go.
 *
 * Built out of the two things that already know the answer:
 * `Container.descendants` for what exists, and `tabOrder` for where focus can
 * land. Using `tabOrder` rather than forming a second opinion is the point -
 * an index that disagreed with Tab about the shape of a window would be worse
 * than no index, because it would be believed.
 *
 * Names come from `describeAccessible`, the same place the screen-reader
 * mirror gets them, so there is one definition of what a thing is called.
 */
export function buildNavigationIndex(
  root: Container,
  active: Control | null,
  exclude: Control | null = null,
): NavEntry[] {
  const out: NavEntry[] = [];
  const windows = [...root.controls].filter(isDesktopWindow);

  // Active window first, then the rest in reverse z-order, so the list reads
  // front to back the way the screen looks.
  const ordered = [
    ...windows.filter((w) => (w as unknown as Control) === active),
    ...[...windows].reverse().filter((w) => (w as unknown as Control) !== active),
  ];

  for (const win of ordered) {
    const control = win as unknown as Control;
    const node = control.describeAccessible();
    // A parked window is real and restorable, and a keyboard-only user has no
    // other way to reach one.
    const minimized = (win as Minimizable).minimized === true;
    out.push({
      control,
      kind: "window",
      depth: 0,
      name: node?.name || "Untitled",
      role: node?.role ?? "region",
      active: control === active,
      ...(minimized ? { minimized: true } : {}),
    });
    if (minimized) continue;
    collectInside(out, control as Container);
  }

  // Anything on the desktop that is not a window - icons, a taskbar, a
  // notification host - listed after the windows rather than dropped.
  for (const child of root.controls) {
    if (isDesktopWindow(child) || child === exclude) continue;
    const container = child as Container;
    if (container.controls) {
      collectInside(out, container, 0);
      continue;
    }
    // A desktop icon is a bare Control with no children, so the walk inside
    // finds nothing and the icon itself would go unlisted - which is most of
    // what is on an empty desktop.
    if (!child.effectivelyVisible) continue;
    const node = child.describeAccessible();
    if (node) {
      out.push({ control: child, kind: "stop", depth: 0, name: node.name || roleWord(node.role), role: node.role });
    }
  }

  return out;
}

function collectInside(out: NavEntry[], scope: Container, depth = 1): void {
  if (!scope.controls) return;
  const seen = new Set<Control>();

  for (const control of scope.controls.descendants()) {
    if (!control.effectivelyVisible) continue;
    const node = control.describeAccessible();
    if (!node || !LANDMARKS.has(node.role)) continue;
    seen.add(control);
    out.push({
      control,
      kind: "landmark",
      depth,
      name: node.name || roleWord(node.role),
      role: node.role,
    });
  }

  for (const control of tabOrder(scope)) {
    if (seen.has(control)) continue;
    const node = control.describeAccessible();
    if (!node) continue;
    out.push({
      control,
      kind: "stop",
      depth,
      name: node.name || roleWord(node.role),
      role: node.role,
    });
  }
}

/**
 * A name for something nobody named, so a row is never blank.
 *
 * Words rather than role tokens. An index reading "menubar, toolbar, tree,
 * listbox, status" is a dump of the implementation; the point of the thing is
 * to be read by somebody in a hurry.
 */
const ROLE_WORDS: Partial<Record<AccessibleRole, string>> = {
  menubar: "Menu bar",
  toolbar: "Toolbar",
  tablist: "Tabs",
  status: "Status bar",
  tree: "Tree",
  listbox: "List",
  grid: "Grid",
  region: "Panel",
  group: "Group",
  none: "Item",
};

function roleWord(role: AccessibleRole): string {
  return ROLE_WORDS[role] ?? role;
}

/**
 * The entries matching a query, keeping the window each match sits in.
 *
 * Dropping the window would leave a flat list of captions with no way to tell
 * which of three open documents each belongs to, which is the question the
 * index exists to answer.
 */
export function filterNavigationIndex(entries: readonly NavEntry[], query: string): NavEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...entries];

  const out: NavEntry[] = [];
  let window: NavEntry | null = null;
  let emitted = false;

  for (const entry of entries) {
    if (entry.kind === "window") {
      window = entry;
      emitted = false;
    }
    if (!entry.name.toLowerCase().includes(needle)) continue;
    if (entry.kind !== "window" && window && !emitted) {
      out.push(window);
      emitted = true;
    }
    if (entry.kind === "window") emitted = true;
    out.push(entry);
  }
  return out;
}

/** The window an entry belongs to, for activating before focusing. */
export function windowOf(entry: NavEntry): DesktopWindow | null {
  let c: Control | null = entry.control;
  while (c) {
    if (isDesktopWindow(c)) return c;
    c = c.parent;
  }
  return null;
}
